import { useEffect, useState } from "react";
import styled from "styled-components";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import AllyService from "../../../services/ally.service";
import { useGlobal } from "../../../context/global";
import { AllyMySettlement, AllyMySettlements, AllySettlementPassenger } from "../../../types/common.types";
import m, { desktop } from "../../../styles/molanturTheme";
import { Badge, Card, Icon, Label, TourThumb, clp, dateFromIso } from "../ui";

// Liquidaciones: lo que el aliado le debe a la agencia y cómo lo va pagando.
//   - Arriba, los totales de TODO: adeudado (tours ya realizados + lo ya cobrado), pagado y por pagar.
//     Sirve para un pago general: el aliado ve de una vez cuánto le falta.
//   - "Sin liquidar todavía": tours ya realizados que la agencia todavía no le cobró.
//   - Cada liquidación (pago pendiente que crea la agencia): total, pagado, saldo para cerrarla, los
//     pagos recibidos con su fecha, y los pasajeros que cubre.

const day = (iso: string) => format(dateFromIso(iso), "d MMM", { locale: es })
// "Lighthouse Atacama · 21/09 al 04/10" → "21/09 al 04/10": el aliado ya sabe que es suyo.
const periodOf = (name: string) => name.split(' · ').slice(1).join(' · ') || name

export default function Settlements() {
    const { setIsLoading } = useGlobal()
    const [data, setData] = useState<AllyMySettlements>()
    const [open, setOpen] = useState<string[]>([])

    useEffect(() => {
        async function load() {
            setIsLoading(true)
            try {
                const res = await AllyService.listMySettlements()
                setData(res.data)
            } catch (error) {
                console.log(error)
            } finally {
                setIsLoading(false)
            }
        }
        load()
    }, [setIsLoading])

    const toggle = (id: string) => setOpen(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
    const isEmpty = data && data.settlements.length === 0 && data.unbilled.rows.length === 0

    return (
        <Main>
            <Label>Pagos</Label>
            <h1>Liquidaciones</h1>
            {data && <p className="as-of">Al {format(dateFromIso(data.as_of), "d 'de' MMMM yyyy", { locale: es })}</p>}

            {data && (
                <div className="totals">
                    <Card $pad={18}>
                        <Label>Adeudado</Label>
                        <strong>{clp(data.totals.total)}</strong>
                        <span className="hint">Tours realizados y ya cobrados</span>
                    </Card>
                    <Card $pad={18} className="paid">
                        <Label>Pagado</Label>
                        <strong>{clp(data.totals.paid)}</strong>
                    </Card>
                    <Card $pad={18} className={data.totals.due > 0.5 ? 'due' : 'clear'}>
                        <Label>Por pagar</Label>
                        <strong>{clp(data.totals.due)}</strong>
                        <span className="hint">{data.totals.due > 0.5 ? 'Puedes pagarlo todo de una vez' : 'Estás al día'}</span>
                    </Card>
                </div>
            )}

            {isEmpty && (
                <Card className="empty">
                    <span className="icon"><Icon name="wallet" size={30} stroke={1.7} /></span>
                    <strong>Todavía no hay liquidaciones para mostrar</strong>
                    <p>Cuando tus pasajeros hagan sus tours, aquí verás lo que corresponde pagar a Molantur y tus pagos.</p>
                </Card>
            )}

            {data && data.unbilled.rows.length > 0 && (
                <section>
                    <div className="section-head">
                        <h2>Sin liquidar todavía</h2>
                        <span className="amount">{clp(data.unbilled.total)}</span>
                    </div>
                    <p className="section-note">Tours ya realizados que se incluirán en tu próxima liquidación.</p>
                    <Card $pad={0}>
                        <PassengerList rows={data.unbilled.rows} />
                    </Card>
                </section>
            )}

            {data && data.settlements.length > 0 && (
                <section>
                    <div className="section-head"><h2>Liquidaciones</h2></div>
                    <div className="list">
                        {data.settlements.map(s => (
                            <SettlementCard key={s.id} s={s} isOpen={open.includes(s.id)} onToggle={() => toggle(s.id)} />
                        ))}
                    </div>
                </section>
            )}
        </Main>
    )
}

function SettlementCard({ s, isOpen, onToggle }: { s: AllyMySettlement, isOpen: boolean, onToggle: () => void }) {
    const closed = s.status === 'closed' || s.balance < 0.5
    return (
        <Card $pad={0} className="settlement" role="group" aria-label={`Liquidación ${periodOf(s.name)}`}>
            <div className="settlement-head">
                <div>
                    <Label>Liquidación</Label>
                    <h3>{periodOf(s.name)}</h3>
                </div>
                <Badge $tone={closed ? 'success' : 'warn'}>{closed ? 'Pagada' : 'Por pagar'}</Badge>
            </div>
            <div className="figures">
                <div><Label>Total</Label><strong>{clp(s.total)}</strong></div>
                <div><Label>Pagado</Label><strong className="paid">{clp(s.paid)}</strong></div>
                <div><Label>Para cerrar</Label><strong className={closed ? 'clear' : 'due'}>{clp(s.balance)}</strong></div>
            </div>
            <div className="payments">
                <Label>Pagos recibidos</Label>
                {s.payments.length === 0
                    ? <p className="none">Todavía no hay pagos registrados.</p>
                    : (
                        <ul>
                            {s.payments.map((p, i) => (
                                <li key={`${p.date}-${i}`}>
                                    <span>{format(dateFromIso(p.date), "d 'de' MMMM", { locale: es })}</span>
                                    <strong>{clp(p.amount)}</strong>
                                </li>
                            ))}
                        </ul>
                    )}
            </div>
            <div className="foot">
                <button type="button" className="more" aria-expanded={isOpen} onClick={onToggle}>
                    {isOpen ? 'Ocultar pasajeros' : `Ver pasajeros (${s.passengers.length})`}
                    <Icon name="chevronDown" size={14} stroke={2.2} style={{ transform: isOpen ? 'rotate(180deg)' : 'none' }} />
                </button>
            </div>
            {isOpen && <PassengerList rows={s.passengers} />}
        </Card>
    )
}

function PassengerList({ rows }: { rows: AllySettlementPassenger[] }) {
    return (
        <ul className="pax-list">
            {rows.map(r => (
                <li key={r.reservation_id}>
                    <span className="date">{day(r.reservation_date)}</span>
                    <TourThumb tourId={r.tour_id} size={24} />
                    <span className="who">
                        <span className="name">{r.passenger_name}</span>
                        <span className="tour">{r.tour_id}</span>
                    </span>
                    <span className="amount">{clp(r.amount)}</span>
                </li>
            ))}
        </ul>
    )
}

const Main = styled.div`
    h1 {
        margin: 4px 0 4px;
        font-family: ${m.font.display};
        font-weight: 600;
        font-size: 26px;
        line-height: 1.1;
        color: ${m.color.ink};
    }
    .as-of {
        margin: 0 0 16px;
        font-size: 13px;
        color: ${m.color.inkFaint};
    }
    .totals {
        display: grid;
        grid-template-columns: 1fr;
        gap: 10px;
        margin-bottom: 22px;
        strong {
            display: block;
            margin-top: 6px;
            font-family: ${m.font.display};
            font-size: 26px;
            font-weight: 600;
            color: ${m.color.ink};
        }
        .hint {
            display: block;
            margin-top: 2px;
            font-size: 12px;
            color: ${m.color.inkFaint};
        }
        .paid strong { color: ${m.color.success}; }
        .due strong { color: ${m.color.warn}; }
        .clear strong { color: ${m.color.success}; }
    }
    section { margin-bottom: 22px; }
    .section-head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 10px;
        h2 {
            margin: 0 0 6px;
            font-family: ${m.font.display};
            font-weight: 600;
            font-size: 20px;
            color: ${m.color.ink};
        }
        .amount { font-weight: 600; color: ${m.color.warn}; }
    }
    .section-note {
        margin: 0 0 10px;
        font-size: 12.5px;
        color: ${m.color.inkFaint};
    }
    .list {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
    .settlement-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 10px;
        padding: 16px 16px 0;
        h3 {
            margin: 4px 0 0;
            font-family: ${m.font.display};
            font-weight: 600;
            font-size: 19px;
            color: ${m.color.ink};
        }
    }
    .figures {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
        padding: 14px 16px;
        strong {
            display: block;
            margin-top: 4px;
            font-size: 15px;
            font-weight: 600;
            color: ${m.color.ink};
            &.paid, &.clear { color: ${m.color.success}; }
            &.due { color: ${m.color.warn}; }
        }
    }
    .payments {
        margin: 0 16px;
        padding: 12px 0;
        border-top: 1px solid ${m.color.line};
        ul {
            list-style: none;
            margin: 8px 0 0;
            padding: 0;
        }
        li {
            display: flex;
            justify-content: space-between;
            padding: 4px 0;
            font-size: 13.5px;
            color: ${m.color.inkSoft};
            strong { color: ${m.color.ink}; font-weight: 600; }
        }
        .none {
            margin: 6px 0 0;
            font-size: 13px;
            color: ${m.color.inkFaint};
        }
    }
    .foot {
        padding: 8px 16px 12px;
        border-top: 1px solid ${m.color.line};
    }
    button.more {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px 0;
        border: none;
        background: none;
        cursor: pointer;
        font-family: ${m.font.label};
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: ${m.color.primaryDeep};
    }
    .pax-list {
        list-style: none;
        margin: 0;
        padding: 4px 16px 10px;
        li {
            display: grid;
            grid-template-columns: 54px 24px 1fr auto;
            align-items: center;
            gap: 10px;
            padding: 9px 0;
            border-top: 1px solid ${m.color.line};
            &:first-child { border-top: none; }
        }
        .date {
            font-family: ${m.font.label};
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
            color: ${m.color.inkSoft};
        }
        .who {
            display: flex;
            flex-direction: column;
            min-width: 0;
        }
        .name { font-size: 13.5px; font-weight: 500; color: ${m.color.ink}; }
        .tour {
            font-size: 12px;
            color: ${m.color.inkFaint};
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .amount { font-size: 13.5px; font-weight: 600; color: ${m.color.ink}; }
    }
    .empty {
        padding: 40px 20px;
        text-align: center;
        strong {
            display: block;
            margin-top: 10px;
            font-weight: 600;
        }
        p {
            margin: 4px auto 0;
            max-width: 360px;
            font-size: 13px;
            line-height: 1.45;
            color: ${m.color.inkFaint};
        }
    }
    .icon {
        display: flex;
        justify-content: center;
        color: ${m.color.inkFaint};
    }
    ${desktop} {
        h1 { font-size: 38px; }
        .totals { grid-template-columns: repeat(3, 1fr); }
        .figures strong { font-size: 18px; }
    }
`
